import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';

import { Button } from 'components/common';
import {
  TextField,
  PasswordField,
  PasswordFieldWithStrength,
  PhoneNumberField,
  CheckboxField,
} from 'components/formFields';
import { FormManager, Form } from 'lib/form';
import { useAuth } from 'lib/auth';
import yup from 'lib/validator';
import KycDetails from 'lib/account/kycDetails';
import datetime from 'lib/datetime';

const initialValues = {
  first_name: '',
  last_name: '',
  phone_number: '',
  email: '',
  password: '',
  repeatPassword: '',
  gender: 'male',
  is_subscribe_to_mailchimp: true,
};

const validationSchema = yup.object({
  first_name: yup.string().required('Required'),
  last_name: yup.string().required('Required'),
  phone_number: yup.string().phone().required('Required'),
  email: yup.string().email().required('Required'),
  password: yup.string().required('Required'),
  repeatPassword: yup
    .string()
    .oneOf(
      [yup.ref('password'), null],
      'Just like the weights on both sides of the barbell, your password needs to match!',
    )
    .required('Required'),
  is_subscribe_to_mailchimp: yup.bool(),
  birthday: yup
    .date()
    .nullable()
    .typeError('Invalid Birthday')
    .max(datetime().subtract(1, 'day').toDate(), (params) => {
      return `Birthday must be at earlier than ${datetime(
        params.max,
      ).format('YYYY-MM-DD')}`;
    }),
});

const RegisterForm = ({ redirect, isFormCheckOutPage }) => {
  const { register } = useAuth();

  const handleOnSubmit = async (
    {
      first_name,
      last_name,
      phone_number,
      email,
      password,
      gender,
      goals,
      birthday,
      is_subscribe_to_mailchimp,
    },
    actions,
  ) => {
    try {
      await register({
        redirect,
        firstName: first_name,
        lastName: last_name,
        phoneNumber: phone_number,
        email,
        password,
        is_subscribe_to_mailchimp,
        kycData: {
          gender,
          goals,
          birthday,
          is_subscribe_to_mailchimp: !!is_subscribe_to_mailchimp,
        },
      });

      return;
    } catch (err) {
      actions.setErrors(err.errors);
    }
  };

  return (
    <FormManager
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({ values, isSubmitting, setFieldValue }) => (
        <Form>
          <TextField name="first_name" label="First Name" required />

          <TextField name="last_name" label="Last Name" required />

          <PhoneNumberField
            name="phone_number"
            label="Phone Number"
            required
            info={
              <div className="tw-flex tw-items-center tw-gap-2">
                <i className="bi bi-whatsapp tw-text-[#25d366]" />
                <span className="tw-text-xl tw-text-[#666]">
                  Enter a WhatsApp number for delivery updates
                </span>
              </div>
            }
          />

          <TextField name="email" label="Email address" required />

          <PasswordFieldWithStrength
            name="password"
            type="password"
            label="Password"
            required
          />

          <PasswordField
            name="repeatPassword"
            label="Repeat Password"
            helperProps={{ className: 'tw-leading-7 tw-mt-2' }}
            required
          />

          {isFormCheckOutPage && (
            <KycDetails values={values} setValue={setFieldValue} />
          )}

          <Row>
            <Col>
              <CheckboxField
                name="is_subscribe_to_mailchimp"
                label="Get important order cut-off notices, the latest dishes, meal planner upgrades, & exclusive offers in your inbox"
              />
            </Col>
          </Row>

          <Button
            type="submit"
            variant="outlined"
            loading={isSubmitting}
          >
            <span>Register</span>
            <i className="icon-long-arrow-right"></i>
          </Button>
        </Form>
      )}
    </FormManager>
  );
};

RegisterForm.propTypes = {
  redirect: PropTypes.bool,
  isFormCheckOutPage: PropTypes.bool,
};

RegisterForm.defaultProps = {
  redirect: true,
  isFormCheckOutPage: false,
};

export default RegisterForm;
